import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useApolloClient } from 'hooks/useApolloClient'
import { useMutation, useQuery } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
// graphql
import { RISK_VIEW_DELETE_MUTATION } from 'graphql/risk/mutations'
import { RISK_VIEW_RISKS_FETCH_MORE_QUERY, RISK_VIEW_RISKS_QUERY } from 'graphql/risk/queries'
// types
import { TRefetch } from 'types/refetch'
import {
  IRisk,
  IFilter,
  IOrderInput,
  IFieldFilter,
  IRiskResponse,
  IRisksResponse,
  IRisksQueryArgs
} from 'types/graphql'
// context
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
// store
import { offlineRisksSelector } from 'store/reducers/offlineRisk'
import { deleteRiskView as deleteRiskViewAction } from 'store/reducers/riskViews'
// hooks
import { useRisksAPI } from 'hooks/risks/useRiskAPI'
import { useShowModal } from 'hooks/useShowModal'
import { useFormsLinking } from 'hooks/risks'
import { useRiskViewTable } from 'components/PageComponents/Risks/Register/components/RiskView/hooks/useRiskViewTable'
import { useRiskViewFilters } from 'components/PageComponents/Risks/Register/components/RiskView/components/RisksContainer/components/RiskContainer/components/Filters/hooks/useRiskViewFilters'
// constants
import { Modals } from 'constants/modals'
// utils
import { delay } from 'utils/helpers'
import { handleError } from 'utils/errors/handlers'
import { debounce, get, omit, orderBy } from 'lodash'

type Props = React.PropsWithChildren<unknown>

type RiskToLink = IRisk & {
  action: 'copy' | 'reference'
}

type RisksQueryResponse = { risks: IRisksResponse }

interface IContext {
  page: number
  risks: IRisk[]
  order: IOrderInput | null
  filters: IFilter[]
  perPage: number
  refetch: TRefetch<IRiskResponse, IRisksQueryArgs>
  loadMore: (page: number) => Promise<void>
  setOrder: (order: IOrderInput) => void
  setSearch: (value: string) => void
  isLoading: boolean
  setPerPage: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
  currentTab: string | null
  totalCount: number
  createRisk: () => Promise<void>
  deleteRisk: (id: string) => () => Promise<void>
  updateRisks: (data: IRisk[]) => void
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
  setCurrentTab: (value: string | null) => void
  filtersRefetch: () => void
  appliedFilters: IFieldFilter[] | null
  deleteRiskView: () => void
  isInfiniteLoader: boolean
  setAppliedFilters: (fields: IFieldFilter[] | null, filterId?: string) => void

  risksToLink: RiskToLink[]
  setRisksToLink: React.Dispatch<React.SetStateAction<RiskToLink[]>>
}

export const RiskViewAPIContext = React.createContext<IContext>({} as any)

const RiskViewAPI: React.FC<Props> = ({ children }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { client } = useApolloClient()

  const { isFormView } = useFormsLinking()

  const { createRisk: riskCreateMutation, deleteRisk: deleteRiskMutation } = useRisksAPI()

  const [risks, setRisks] = React.useState<IRisk[]>([])
  const [risksToLink, setRisksToLink] = React.useState<RiskToLink[]>([])

  const { riskView, isScheduled, selectRiskView } = React.useContext(RiskRegisterContext)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_VIEW_MODAL })

  const {
    page,
    order,
    search,
    perPage,
    setSearch,
    currentTab,
    onPageChange,
    appliedFilters,
    handleSetOrder,
    isInfiniteLoader,
    handleSetPerPage,
    handleSetCurrentTab,
    setIsInfiniteLoader,
    handleSetAppliedFilters
  } = useRiskViewTable()

  const { filters, refetch: filtersRefetch } = useRiskViewFilters()

  const {
    data: risksQuery,
    refetch,
    loading: isLoading
  } = useQuery(RISK_VIEW_RISKS_QUERY, {
    skip: !riskView,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      order,
      filter: {
        search,
        isLinkedView: isFormView,
        riskViewId: riskView?.id,
        fieldFilters: appliedFilters?.map((item) => omit(item, 'originalValue')) || [],
        recurrence: isScheduled,
        riskTemplateId: riskView?.riskTemplate.id,
        ...(router.query.riskId && { ids: [router.query.riskId] })
      },
      pagination: {
        pageSkip: perPage * page,
        pageTake: perPage
      }
    }
  })

  const [riskViewDeleteMutation] = useMutation(RISK_VIEW_DELETE_MUTATION)

  const offlineRisks = useSelector(offlineRisksSelector)

  // set initial risks to state
  React.useEffect(() => {
    const risks = get(risksQuery, 'risks.data', [])
    const isInfiniteLoader = get(risksQuery, 'risks.pagination.isInfiniteLoader', false)

    setIsInfiniteLoader(isInfiniteLoader)

    if (navigator.onLine) {
      setRisks(risks)
    }
  }, [risksQuery])

  // set initial risk to state when offline
  React.useEffect(() => {
    if (!navigator.onLine && riskView) {
      const risks = orderBy(
        offlineRisks?.filter((risk) => risk?.riskTemplate.id === riskView?.riskTemplate.id),
        'createdAt',
        'desc'
      )

      setRisks(risks)
    }
  }, [riskView])

  // refetch riskView when back to online
  React.useEffect(() => {
    const handleOnline = async () => {
      await delay(1000)
      refetch()
    }

    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  const totalCount = get(risksQuery, 'risks.pagination.totalCount', 0)

  const loadMore: IContext['loadMore'] = async (nextPage) => {
    const { data: newData } = await client.query<RisksQueryResponse>({
      query: RISK_VIEW_RISKS_FETCH_MORE_QUERY,
      variables: {
        order,
        filter: {
          riskViewId: riskView?.id,
          recurrence: isScheduled,
          fieldFilters: appliedFilters?.map((item) => omit(item, 'originalValue')) || [],
          riskTemplateId: riskView?.riskTemplate.id
        },
        pagination: {
          pageSkip: nextPage * 10,
          pageTake: 10
        }
      },
      fetchPolicy: 'network-only'
    })

    if (newData?.risks?.data?.length) {
      setRisks((prev) => [...prev, ...newData.risks.data])
    }
  }

  const deleteRiskView = () => {
    showModal({
      width: 'sm',
      handleConfirm: async () => {
        try {
          await riskViewDeleteMutation({
            variables: {
              id: riskView.id
            }
          })

          dispatch(deleteRiskViewAction(riskView.id))
          selectRiskView(null)
        } catch (e) {
          handleError(e, 'RiskViewAPI.deleteRiskView')
        }
      }
    })
  }

  const createRisk = async () => {
    try {
      const { id } = await riskCreateMutation({
        riskTemplateId: riskView.riskTemplate.id,
        ...(isScheduled && { recurrence: { active: false } })
      })

      router.push(`/forms/edit/${id}/template/${riskView.riskTemplate.id}`)
    } catch (e) {
      handleError(e, 'RiskViewAPI.createRisk')
    }
  }

  const deleteRisk: IContext['deleteRisk'] = (id) => async () => {
    try {
      await deleteRiskMutation(id)

      setRisks((prev) => prev.filter((risk) => risk.id !== id))
    } catch (e) {
      handleError(e, 'RiskViewAPI.deleteRisk')
    }
  }

  const updateRisks = (data: IRisk[]) => setRisks(data)

  const debouncedSearch = React.useCallback(
    debounce((value: string) => setSearch(value), 400),
    []
  )

  const value = {
    page,
    order,
    risks,
    filters,
    perPage,
    refetch,
    loadMore,
    isLoading,
    setSearch: debouncedSearch,
    totalCount,
    currentTab,
    createRisk,
    deleteRisk,
    updateRisks,
    onPageChange,
    filtersRefetch,
    appliedFilters,
    deleteRiskView,
    isInfiniteLoader,
    setOrder: handleSetOrder,
    setPerPage: handleSetPerPage,
    setCurrentTab: handleSetCurrentTab,
    setAppliedFilters: handleSetAppliedFilters,
    risksToLink,
    setRisksToLink
  }

  return <RiskViewAPIContext.Provider value={value}>{children}</RiskViewAPIContext.Provider>
}

export default RiskViewAPI
